import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { toast } from 'sonner';
import { TreePine, Check } from 'lucide-react';

const EMPTY_FORM = { name: '', birth_year: '', death_year: '', gender: 'other', user_id: '' };

const titleMap = {
  edit: 'Edit Person',
  'add-parent': 'Add Parent',
  'add-child': 'Add Child',
  'add-partner': 'Add Spouse / Partner',
  'add-root': 'Add Yourself',
};

function MemberPicker({ members, selectedId, onPick }) {
  return (
    <div className="pt-4 border-t border-gray-100" data-testid="tree-member-picker">
      <p className="text-sm font-medium text-gray-700">Or pick someone who has a Legacy Forge login</p>
      <p className="text-xs text-gray-500 mb-2">Their name fills in above. Nothing is added until you tap "Add to Tree".</p>
      <div className="space-y-1.5 max-h-48 overflow-y-auto">
        {members.map(m => {
          const selected = selectedId === m.user_id;
          return (
            <button
              key={m.user_id}
              type="button"
              onClick={() => onPick(selected ? null : m)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg border text-left transition-colors ${selected ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50'}`}
              data-testid={`pick-member-${m.user_id}`}
              aria-pressed={selected}
            >
              <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {(m.name || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 text-sm truncate">{m.name}</p>
                {m.email && <p className="text-xs text-gray-500 truncate">{m.email}</p>}
              </div>
              {selected && <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function NodeFormModal({ open, onClose, onSave, initialData, mode, familyMembers, existingNodeUserIds }) {
  const [form, setForm] = useState(EMPTY_FORM);

  const availableMembers = (familyMembers || []).filter(
    m => m.user_id && !existingNodeUserIds?.has(m.user_id)
  );

  useEffect(() => {
    if (!open) return;
    setForm(initialData ? {
      name: initialData.name || '',
      birth_year: initialData.birth_year || '',
      death_year: initialData.death_year || '',
      gender: initialData.gender || 'other',
      user_id: initialData.user_id || '',
    } : EMPTY_FORM);
  }, [initialData, open]);

  const pickMember = (m) => setForm(f => ({ ...f, name: m ? m.name || '' : '', user_id: m ? m.user_id : '' }));

  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    onSave(form);
  };

  const showPicker = mode !== 'edit' && availableMembers.length > 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="tree-node-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TreePine className="w-5 h-5 text-emerald-600" />
            {titleMap[mode] || 'Add Person'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Name *</label>
            <Input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value, user_id: mode === 'edit' ? f.user_id : '' }))}
              placeholder="Full name"
              data-testid="tree-node-name-input"
              autoFocus
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Gender</label>
            <Select value={form.gender} onValueChange={v => setForm(f => ({ ...f, gender: v }))}>
              <SelectTrigger data-testid="tree-node-gender-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Birth Year</label>
              <Input
                value={form.birth_year}
                onChange={e => setForm(f => ({ ...f, birth_year: e.target.value }))}
                placeholder="e.g. 1965"
                data-testid="tree-node-birth-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Passed Year</label>
              <Input
                value={form.death_year}
                onChange={e => setForm(f => ({ ...f, death_year: e.target.value }))}
                placeholder="Leave blank if living"
                data-testid="tree-node-death-input"
              />
            </div>
          </div>

          {showPicker && <MemberPicker members={availableMembers} selectedId={form.user_id} onPick={pickMember} />}

          <DialogFooter>
            <Button variant="outline" onClick={onClose} data-testid="tree-modal-cancel">Cancel</Button>
            <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tree-modal-save">
              {mode === 'edit' ? 'Update' : 'Add to Tree'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
